const API_URL = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3001";

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...options,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message ?? `Request failed: ${res.status}`);
  }

  return res.json();
}

async function uploadFile<T>(path: string, formData: FormData): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    method: "POST",
    credentials: "include",
    body: formData,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message ?? `Upload failed: ${res.status}`);
  }

  return res.json();
}

export interface AiConfig {
  nomeAssistente: string;
  nomeNegocio: string;
  segmentoNegocio: string;
  infoNegocio: string;
  regrasAtivas: string[];
}

export interface DefaultRule {
  id: string;
  label: string;
  description: string;
  category: "conduta" | "tom" | "vendas" | "formato";
}

export interface AuthUser {
  id: string;
  nome: string;
  email: string;
  funcao: "cliente" | "admin";
  dataCriado: string;
  aiConfig: AiConfig | null;
}

export interface ConversationData {
  id: string;
  customerPhone: string;
  customerName: string | null;
  lastMessageAt: string;
  lastMessagePreview: string | null;
  lastMessageRole: string | null;
  unreadCount: number;
  createdAt: string;
}

export interface MessageData {
  id: string;
  conversationId: string;
  role: "customer" | "assistant";
  content: string;
  createdAt: string;
}

export interface ConversationWithMessages extends ConversationData {
  messages: MessageData[];
}

export interface DashboardStats {
  activePhone: string | null;
  isConnected: boolean;
  totalConversations: number;
  messagesReceivedToday: number;
  messagesSentToday: number;
  aiResponsesToday: number;
  recentConversations: ConversationData[];
  weeklyData: { date: string; day: string; ia: number }[];
}

export interface WhatsAppProfile {
  about: string;
  description: string;
  profilePictureUrl: string | null;
}

export interface DisplayNameInfo {
  verifiedName: string;
  nameStatus: string;
  newDisplayName: string | null;
  newNameStatus: string | null;
}

export const api = {
  auth: {
    login: (email: string, senha: string) =>
      request<{ user: AuthUser }>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, senha }),
      }),

    register: (nome: string, email: string, senha: string) =>
      request<{ user: AuthUser }>("/auth/register", {
        method: "POST",
        body: JSON.stringify({ nome, email, senha }),
      }),

    me: () => request<{ user: AuthUser }>("/auth/me"),

    logout: () =>
      request<{ success: boolean }>("/auth/logout", { method: "POST" }),

    updateProfile: (data: { nome?: string; email?: string; senha?: string; aiConfig?: AiConfig }) =>
      request<{ user: AuthUser }>("/auth/profile", {
        method: "PUT",
        body: JSON.stringify(data),
      }),
  },

  ai: {
    getRules: () => request<DefaultRule[]>("/chat/rules"),

    testStream: async function* (messages: { role: "user" | "assistant"; content: string }[]) {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3001"}/chat/test`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ messages }),
        },
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message ?? `Request failed: ${res.status}`);
      }

      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith("data: ")) continue;
          const payload = trimmed.slice(6);
          if (payload === "[DONE]") return;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.content) yield parsed.content as string;
          } catch {}
        }
      }
    },
  },

  conversations: {
    list: () => request<ConversationData[]>("/conversations"),

    get: (id: string) =>
      request<ConversationWithMessages>(`/conversations/${id}`),

    stats: () => request<DashboardStats>("/conversations/stats"),

    markRead: (id: string) =>
      request<void>(`/conversations/${id}/read`, { method: "POST" }),
  },

  whatsapp: {
    getProfile: () =>
      request<WhatsAppProfile>("/meta/business-profile"),

    updateProfile: (data: { about?: string; description?: string }) =>
      request<{ success: boolean }>("/meta/business-profile", {
        method: "PUT",
        body: JSON.stringify(data),
      }),

    uploadPhoto: (file: File) => {
      const formData = new FormData();
      formData.append("photo", file);
      return uploadFile<{ success: boolean }>("/meta/business-profile/photo", formData);
    },

    manualSetup: (data: { wabaId: string; businessId: string }) =>
      request<{ success: boolean; phoneNumberId: string; wabaId: string; businessId: string }>(
        "/meta/manual-setup",
        { method: "POST", body: JSON.stringify(data) },
      ),

    getDisplayName: () =>
      request<DisplayNameInfo>("/meta/display-name"),

    updateDisplayName: (name: string) =>
      request<{ success: boolean }>("/meta/display-name", {
        method: "PUT",
        body: JSON.stringify({ name }),
      }),
  },
};
